import { FC, useMemo, FormEvent } from 'react'
import Form from '@components/forms/form'
import FormGroup from '@components/forms/form_group'
import CopyableInput from '@components/forms/copyable_input'
import Label from '@components/forms/label'
import SVGIcon from '@components/SVGIcon'

interface LinkSharerProps {
  isPublished: boolean
  shareURI: string
  tweetText: string
  resourceType: string
}

const LinkSharer: FC<LinkSharerProps> = ({ isPublished, shareURI, tweetText, resourceType }) => {
  // Helper function to convert resourceType to lowercase
  const getResourceName = useMemo(() => resourceType.toLowerCase(), [resourceType])

  const handleSubmit = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault()
  }

  // Generates the content when sharing is enabled
  const getSharingEnabledContent = () => {
    const uriEncodedShareURI = encodeURI(shareURI)
    const uriEncodedTweetText = encodeURI(tweetText)

    const facebookURI = `https://www.facebook.com/sharer.php?u=${uriEncodedShareURI}`
    const xURI = `https://x.com/intent/tweet?url=${uriEncodedShareURI}&text=${uriEncodedTweetText}`
    const pinterestURI = `https://pinterest.com/pin/create/button?url=${uriEncodedShareURI}`

    // Avoid autofocus as the component might be rendered inside a popover
    return (
      <>
        <ul className="unstyled-list link-sharer__social-providers">
          <li className="providers__provider">
            <a
              href={xURI}
              className="social-link social-link--x hint hint--bottom-right hint--timetoast"
              data-hint="Share to X"
              aria-label={`Share ${getResourceName} on X`}
            >
              <SVGIcon alt="X logo" className="social-link__icon" xlinkHref="#social-icons-x-logo" />
            </a>
          </li>
          <li className="providers__provider">
            <a
              href={facebookURI}
              className="social-link social-link--facebook hint hint--bottom-right hint--timetoast"
              data-hint="Share to Facebook"
              aria-label={`Share ${getResourceName} on Facebook`}
            >
              <SVGIcon
                className="social-link__icon"
                alt="Facebook logo"
                pointerEvents="none"
                xlinkHref="#social-icons-facebook-logo"
              />
            </a>
          </li>
          <li className="providers__provider">
            <a
              href={pinterestURI}
              className="social-link social-link--pinterest hint hint--bottom-right hint--timetoast"
              data-hint="Pin to Pinterest"
              aria-label={`Pin ${getResourceName} on Pinterest`}
            >
              <SVGIcon
                className="social-link__icon"
                alt="Pinterest logo"
                pointerEvents="none"
                xlinkHref="#social-icons-pinterest-logo"
              />
            </a>
          </li>
        </ul>
        <Form className="form" onSubmit={handleSubmit}>
          <FormGroup>
            <Label className="label" htmlFor="public-link-input">
              Public link
            </Label>
            <CopyableInput
              id="public-link-input"
              inputClassName="input"
              buttonClassName="btn btn--primary"
              value={shareURI}
              autoFocus={false}
              readOnly={false}
            />
          </FormGroup>
        </Form>
      </>
    )
  }

  // Generates the content when sharing is disabled
  const getSharingDisabledContent = () => (
    <div className="notice notice--info p">This {getResourceName} must be published to share using a public link.</div>
  )

  // Render content based on whether the resource is published
  const content = isPublished ? getSharingEnabledContent() : getSharingDisabledContent()

  return <section className="link-sharer p">{content}</section>
}

export default LinkSharer
